/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { guid } from 'app/shared/lib/guid';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import * as UM from 'app/um/models/essette-api';

export interface IAuthAuditLogModelData {
    auth: UM.Auth;
}

@autoinject()
export class AuthAuditLog extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected authId: string | null = null

    protected auditLogSections: Record<string, boolean> = {};

    protected sectionVisibility = {
        authInformation: false,
        diagsAndServices: false,
        admissionInformation: false,
        statusHistory: false,
        routing: false
    }

    protected headerLevelTat: boolean = true;
    protected id: guid = guid();

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: AuthPageContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(model: IAuthAuditLogModelData): Promise<void> {
        if(!model.auth.id) return;
        await this.checkPermissions();

        this.authId = model.auth.id;
    }

    protected bind(): void {
    }

    ////****************** Events ***************************************************////
    protected openSection(sectionId: string): void {
        const section = sectionId.replace('-collapsible', '');
        this.auditLogSections[section] = true;
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.sectionVisibility.authInformation = this.accountContext.hasRouteAccess('um.auth.info');
        this.sectionVisibility.diagsAndServices = this.accountContext.hasRouteAccess('um.auth.diags-services');
        this.sectionVisibility.admissionInformation = this.accountContext.hasRouteAccess('um.auth.care-days');
        this.sectionVisibility.statusHistory = this.accountContext.hasRouteAccess('um.auth.status');
        this.sectionVisibility.routing = this.accountContext.hasRouteAccess('um.auth.routing');
        this.headerLevelTat = !this.context.lineLevelTAT;
    }

    ////****************** Service Calls ********************************************////

}

/**
 * Opens the auth audit log modal with the global dialog
 * @param auth The auth information to use
 */
export function openAuthAuditLog(auth: UM.Auth): void {
    if(!auth || !auth.id) {
        throw new Error('Auth is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: 'um.auth.audit-log',
        title: {
            key: 'um:routes.auth.audit-log.$title',
            replace: { number: auth.number }
        },
        print: true,
        popout: {
            route: 'um.auth.audit-log',
            params: { authId: auth.id }
        }
    };

    const modelData: IAuthAuditLogModelData = {
        auth: auth
    };

    const modalData: IModalData = {
        componentId: 'app/um/components/audit-log/auth-audit-log',
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
